import { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Globe, CheckCircle, ArrowRight, Check, Medal, Target, GanttChart, Network, PieChart, Search, Database, LinkIcon, Phone, BarChart4, ChevronRight, TrendingUp, Layers, Clock, Star, Zap } from "lucide-react";
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SEO from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import ContactForm from '@/components/ContactForm';
import CallToAction from '@/components/CallToAction';

const EnterpriseSeo = () => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        seo={{
          title: "Enterprise SEO Solutions | Los Angeles SEO Group",
          description: "Custom enterprise-grade SEO solutions for large organizations with complex websites, multiple locations, and competitive markets.",
          canonical: "https://losangelesseogroup.com/services/enterprise-seo",
        }}
        url="https://losangelesseogroup.com/services/enterprise-seo"
      />
      <Navbar />
      
      <PageTransition>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-cosmic-nebula/5 to-white dark:from-cosmic-void/20 dark:to-background">
            <div className="page-container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div 
                  initial="hidden" 
                  animate="visible" 
                  variants={fadeIn}
                >
                  <div className="space-y-6">
                    <span className="inline-flex items-center rounded-full bg-cosmic-void/10 dark:bg-cosmic-nebula/10 px-3 py-1 text-sm font-medium text-cosmic-void dark:text-cosmic-nebula">
                      <Building2 className="w-4 h-4 mr-1" />
                      Enterprise SEO
                    </span>
                    <h1 className="text-4xl md:text-5xl font-bold font-heading">
                      Enterprise-Grade 
                      <span className="text-cosmic-nebula block mt-2">
                        SEO Solutions
                      </span>
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-xl">
                      Comprehensive SEO strategies for large organizations with complex websites, 
                      multiple locations, and competitive market positions.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="lg" className="bg-cosmic-gradient">
                            Request Enterprise Consultation
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <div className="space-y-4">
                            <h3 className="text-xl font-bold">Request Enterprise SEO Consultation</h3>
                            <p className="text-sm text-muted-foreground">
                              Fill out the form below and we'll schedule a call with our enterprise team.
                            </p>
                            <ContactForm onSuccess={() => setDialogOpen(false)} />
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <a 
                        href="tel:+13237841577" 
                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-cosmic-void/10 dark:bg-cosmic-nebula/10 text-cosmic-nebula font-medium transition-colors hover:bg-cosmic-void/15 dark:hover:bg-cosmic-nebula/15"
                      >
                        <Phone className="w-5 h-5" />
                        <span>(323) 784-1577</span>
                      </a>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-3">
                      <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center border-2 border-background">
                            <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                          </div>
                        ))}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Trusted by Fortune 500 companies</span>
                        <span className="block text-muted-foreground">across multiple industries</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="lg:px-6"
                >
                  <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
                    <div className="p-6 space-y-5">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-cosmic-void/10 dark:bg-cosmic-nebula/10 flex items-center justify-center">
                            <GanttChart className="w-5 h-5 text-cosmic-nebula" />
                          </div>
                          <div>
                            <h3 className="font-semibold">Enterprise SEO Capabilities</h3>
                            <p className="text-xs text-muted-foreground">Advanced solutions for complex organizations</p>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">
                          Enterprise-Grade
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {[
                          { feature: "Multi-Location Management", details: "Cohesive strategy across all locations" },
                          { feature: "International SEO", details: "Global visibility with local relevance" },
                          { feature: "Custom API Integrations", details: "Seamless data flow between systems" },
                          { feature: "Enterprise Reporting", details: "Comprehensive analytics and insights" }
                        ].map((item, i) => (
                          <div key={i} className="flex justify-between items-center border-b border-border pb-3 last:border-0 last:pb-0">
                            <span className="text-sm font-medium">{item.feature}</span>
                            <div className="text-right">
                              <span className="block text-xs text-muted-foreground">{item.details}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-muted p-4 border-t border-border">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Get a custom enterprise solution</span>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="link" className="text-cosmic-nebula gap-1 p-0">
                              Request a proposal <ChevronRight className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <div className="space-y-4">
                              <h3 className="text-xl font-bold">Request an Enterprise SEO Proposal</h3>
                              <p className="text-sm text-muted-foreground">
                                Tell us about your organization's needs and we'll prepare a custom proposal.
                              </p>
                              <ContactForm />
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Features Section */}
          <section className="py-16 md:py-24 bg-gradient-to-b from-white to-cosmic-nebula/5 dark:from-background dark:to-cosmic-void/10">
            <div className="page-container">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-4xl font-bold font-heading">
                  Enterprise SEO Solutions for <span className="text-cosmic-nebula">Complex Organizations</span>
                </h2>
                <p className="mt-4 text-muted-foreground text-lg">
                  Comprehensive strategies tailored to the unique challenges of large websites and organizations
                </p>
              </div>
              
              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {[
                  {
                    icon: <Layers className="w-6 h-6 text-cosmic-nebula" />,
                    title: "Multi-Site Management",
                    description: "Unified SEO strategy across multiple domains, subdomains, and international sites with centralized management."
                  },
                  {
                    icon: <Globe className="w-6 h-6 text-cosmic-nebula" />,
                    title: "International SEO",
                    description: "Localized strategies for global brands, including hreflang implementation, cultural adaptation, and local market targeting."
                  },
                  {
                    icon: <Database className="w-6 h-6 text-cosmic-nebula" />,
                    title: "API Integrations",
                    description: "Custom API connections to integrate SEO data with your enterprise analytics, CRM, and marketing automation systems."
                  },
                  {
                    icon: <BarChart4 className="w-6 h-6 text-cosmic-nebula" />,
                    title: "Advanced Reporting Suite",
                    description: "Comprehensive reporting dashboard with custom KPIs, competitive intelligence, and actionable insights."
                  },
                  {
                    icon: <Network className="w-6 h-6 text-cosmic-nebula" />,
                    title: "Enterprise-Grade Support",
                    description: "Dedicated account team with priority response times and strategic oversight from senior SEO executives."
                  },
                  {
                    icon: <TrendingUp className="w-6 h-6 text-cosmic-nebula" />,
                    title: "Scalable Solutions",
                    description: "Flexible strategies that grow with your organization and adapt to changing market conditions and business goals."
                  }
                ].map((feature, i) => (
                  <motion.div 
                    key={i}
                    variants={fadeIn}
                    className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-lg bg-cosmic-void/10 dark:bg-cosmic-nebula/10 flex items-center justify-center mb-5">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
          
          {/* Methodology Section */}
          <section className="py-16 md:py-24">
            <div className="page-container">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <span className="inline-flex items-center rounded-full bg-cosmic-void/10 dark:bg-cosmic-nebula/10 px-3 py-1 text-sm font-medium text-cosmic-void dark:text-cosmic-nebula mb-6">
                    <Zap className="w-4 h-4 mr-1" />
                    Our Methodology
                  </span>
                  
                  <h2 className="text-3xl font-bold font-heading mb-6">
                    Enterprise SEO <span className="text-cosmic-nebula">Methodology</span>
                  </h2>
                  
                  <p className="text-muted-foreground mb-8">
                    Our enterprise SEO approach combines strategic vision, technical expertise, and scalable processes to drive meaningful results for large organizations.
                  </p>
                  
                  <Tabs defaultValue="discovery" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="discovery">Discovery</TabsTrigger>
                      <TabsTrigger value="strategy">Strategy</TabsTrigger>
                      <TabsTrigger value="execution">Execution</TabsTrigger>
                      <TabsTrigger value="optimization">Optimization</TabsTrigger>
                    </TabsList>
                    <TabsContent value="discovery" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Comprehensive Technical Audit</p>
                            <p>Full assessment of your technical infrastructure, site architecture, and crawlability issues</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Competitive Intelligence</p>
                            <p>In-depth analysis of market leaders and competitors across all business segments</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Stakeholder Alignment</p>
                            <p>Structured interviews with key stakeholders to understand business objectives and KPIs</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Systems Integration Analysis</p>
                            <p>Review of your technology stack to identify integration opportunities and data flow requirements</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="strategy" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Strategic Roadmap Development</p>
                            <p>Comprehensive 12-24 month SEO strategy with clear milestones and success metrics</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Resource Allocation Planning</p>
                            <p>Detailed resource requirements and budget forecasting for effective implementation</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Cross-Functional Integration</p>
                            <p>Planning for SEO integration with other marketing channels and business units</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Risk Assessment & Mitigation</p>
                            <p>Identification of potential challenges and proactive mitigation strategies</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="execution" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Technical Implementation</p>
                            <p>Systematic execution of technical SEO improvements with minimal business disruption</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Content Development & Optimization</p>
                            <p>Scalable content creation and optimization using enterprise-grade workflows</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Authority Building</p>
                            <p>Strategic link acquisition and digital PR initiatives aligned with brand guidelines</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Training & Knowledge Transfer</p>
                            <p>Customized training programs for internal teams to build SEO capabilities</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="optimization" className="mt-6 space-y-4 text-sm text-muted-foreground">
                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Advanced Analytics & Reporting</p>
                            <p>Comprehensive performance tracking with custom dashboards and executive summaries</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Continuous Optimization</p>
                            <p>Ongoing refinement of strategies based on performance data and market changes</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Algorithm Monitoring & Adaptation</p>
                            <p>Proactive monitoring of algorithm changes with rapid response protocols</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Quarterly Strategy Reviews</p>
                            <p>Comprehensive review sessions with senior stakeholders to ensure alignment</p>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <div className="mt-8">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="lg" className="bg-cosmic-gradient">
                          Request a Custom Strategy
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <div className="space-y-4">
                          <h3 className="text-xl font-bold">Request Your Enterprise SEO Strategy</h3>
                          <p className="text-sm text-muted-foreground">
                            Tell us about your organization's challenges and goals for a customized approach.
                          </p>
                          <ContactForm />
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                
                <motion.div 
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-cosmic-nebula/5 to-cosmic-nebula/20 dark:from-cosmic-void/20 dark:to-cosmic-nebula/20 p-1 rounded-2xl shadow-xl">
                    <div className="bg-card rounded-xl p-8 border border-border">
                      <div className="space-y-6">
                        <div className="flex gap-4 items-start">
                          <div className="w-10 h-10 rounded-full bg-cosmic-void/10 dark:bg-cosmic-nebula/10 flex items-center justify-center">
                            <Medal className="w-5 h-5 text-cosmic-nebula" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold">Enterprise SEO Package</h3>
                            <p className="text-sm text-muted-foreground">Custom solutions for large organizations</p>
                          </div>
                        </div>
                        
                        <div className="pt-2">
                          <div className="text-3xl font-bold">Custom Quote</div>
                          <p className="text-sm text-muted-foreground mt-1">Tailored pricing based on complexity and scope</p>
                        </div>
                        
                        <div className="space-y-3 pt-3">
                          {[
                            "Custom SEO strategy",
                            "Multiple location management",
                            "International SEO",
                            "API integrations",
                            "Advanced reporting suite",
                            "Priority support",
                            "24/7 monitoring"
                          ].map((feature, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-4 space-y-3">
                          <Button className="w-full bg-cosmic-gradient" size="lg" asChild>
                            <Link to="/contact">Request Proposal</Link>
                          </Button>
                          
                          <div className="text-center">
                            <a 
                              href="tel:+13237841577" 
                              className="text-sm text-cosmic-nebula font-medium hover:underline flex items-center justify-center gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              Call for details: (323) 784-1577
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -z-10 -bottom-6 -right-6 w-64 h-64 bg-cosmic-nebula/5 dark:bg-cosmic-nebula/5 rounded-full blur-3xl"></div>
                  <div className="absolute -z-10 -top-6 -left-6 w-48 h-48 bg-cosmic-nebula/10 dark:bg-cosmic-nebula/5 rounded-full blur-3xl"></div>
                </motion.div>
              </div>
            </div>
          </section>
          
          {/* Case Studies Section */}
          <section className="py-16 md:py-24 bg-muted/50">
            <div className="page-container">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl font-bold font-heading">
                  Enterprise <span className="text-cosmic-nebula">Success Stories</span>
                </h2>
                <p className="mt-4 text-muted-foreground">
                  See how we've helped large organizations transform their SEO performance
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    title: "National Retail Chain",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Organic Traffic", value: "+143%" },
                      { label: "Local Visibility", value: "+217%" },
                      { label: "Revenue Growth", value: "+76%" }
                    ],
                    summary: "Unified SEO strategy across 200+ locations with localized content and technical infrastructure improvements.",
                    link: "/case-studies"
                  },
                  {
                    title: "Global B2B Software",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Lead Generation", value: "+92%" },
                      { label: "International Traffic", value: "+156%" },
                      { label: "Domain Authority", value: "+27%" }
                    ],
                    summary: "International SEO strategy with multilingual content and technical optimization for SaaS platform.",
                    link: "/case-studies"
                  },
                  {
                    title: "Healthcare Network",
                    image: "/placeholder.svg",
                    stats: [
                      { label: "Provider Visibility", value: "+183%" },
                      { label: "Appointment Requests", value: "+68%" },
                      { label: "Patient Acquisitions", value: "+45%" }
                    ],
                    summary: "Comprehensive strategy for multi-location healthcare network with provider directories and service pages.",
                    link: "/case-studies"
                  }
                ].map((study, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-video bg-muted relative">
                      <img 
                        src={study.image} 
                        alt={study.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                        <h3 className="text-white font-bold text-xl p-4">{study.title}</h3>
                      </div>
                    </div>
                    
                    <div className="p-5">
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {study.stats.map((stat, j) => (
                          <div key={j} className="text-center">
                            <div className="text-lg font-bold text-cosmic-nebula">{stat.value}</div>
                            <div className="text-xs text-muted-foreground">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        {study.summary}
                      </p>
                      
                      <Link 
                        to={study.link}
                        className="flex items-center text-sm font-medium text-cosmic-nebula hover:text-cosmic-nebula/80"
                      >
                        Read Case Study <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button asChild variant="outline" className="gap-1">
                  <Link to="/case-studies">
                    View All Case Studies <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>
          
          {/* FAQ Section */}
          <section className="py-16 md:py-24">
            <div className="page-container max-w-4xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold font-heading">
                  Frequently Asked Questions About <span className="text-cosmic-nebula">Enterprise SEO</span>
                </h2>
              </div>
              
              <div className="space-y-6">
                {[
                  {
                    q: "How do you handle SEO across multiple locations and websites?",
                    a: "We implement a centralized SEO strategy with localized execution. This includes a consistent technical framework across all sites, geo-specific content strategies, and coordinated implementation that respects brand guidelines while optimizing for local relevance. Our enterprise dashboard gives you visibility across all properties in one place."
                  },
                  {
                    q: "How do you integrate with our existing technology stack and teams?",
                    a: "We begin with a thorough systems analysis to understand your tech ecosystem. Our team develops custom API integrations to connect with your CMS, analytics, and other platforms. We establish clear workflows with your digital, IT, and marketing teams, and provide training to ensure smooth collaboration throughout the engagement."
                  },
                  {
                    q: "What reporting capabilities do you offer for enterprise clients?",
                    a: "Our enterprise reporting suite includes customizable dashboards with real-time data, automated executive summaries, comprehensive competitive analysis, and attribution modeling. We provide both high-level strategic insights for executives and granular tactical data for implementation teams, with scheduled reports and on-demand access."
                  },
                  {
                    q: "How do you approach international SEO for global enterprises?",
                    a: "Our international SEO approach includes proper hreflang implementation, local keyword research in native languages, culturally-adapted content strategies, and technical optimization for regional search engines. We coordinate with your regional teams to ensure local relevance while maintaining global brand consistency and SEO best practices."
                  },
                  {
                    q: "What kind of ROI can we expect from enterprise SEO?",
                    a: "Enterprise SEO typically delivers ROI through multiple channels: increased organic traffic and conversions, reduced PPC spend, improved customer acquisition costs, and enhanced brand authority. While timelines vary based on industry and competitive landscape, our enterprise clients typically see positive ROI within 6-9 months, with compounding returns over time."
                  }
                ].map((faq, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true, margin: "-50px" }}
                    className="bg-card border border-border rounded-lg p-6 shadow-sm"
                  >
                    <h3 className="text-lg font-semibold mb-3">{faq.q}</h3>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10 text-center">
                <p className="text-muted-foreground mb-4">Have additional questions about our enterprise SEO services?</p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <a 
                    href="tel:+13237841577" 
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-cosmic-void/10 dark:bg-cosmic-nebula/10 text-cosmic-nebula font-medium transition-colors hover:bg-cosmic-void/15 dark:hover:bg-cosmic-nebula/15"
                  >
                    <Phone className="w-5 h-5" />
                    <span>(323) 784-1577</span>
                  </a>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button>Contact Us Online</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <div className="space-y-4">
                        <h3 className="text-xl font-bold">Enterprise SEO Questions?</h3>
                        <p className="text-sm text-muted-foreground">
                          Fill out the form below and our enterprise team will contact you.
                        </p>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </section>
          
          {/* CTA Section */}
          <section className="py-16 md:py-20 bg-gradient-to-br from-cosmic-nebula/5 to-cosmic-nebula/10 dark:from-cosmic-void/30 dark:to-cosmic-nebula/10">
            <div className="page-container">
              <div className="max-w-4xl mx-auto bg-card rounded-2xl border border-border overflow-hidden shadow-lg">
                <div className="p-8 md:p-10">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold font-heading">
                      Ready to Transform Your <span className="text-cosmic-nebula">Enterprise SEO</span>?
                    </h2>
                    <p className="mt-3 text-muted-foreground">
                      Schedule a consultation with our enterprise team to discuss your unique challenges
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                    <div className="md:col-span-2">
                      <ContactForm />
                    </div>
                    
                    <div className="bg-muted/60 p-6 rounded-xl border border-border/50">
                      <h3 className="font-semibold mb-3">Or Call Us Directly</h3>
                      <a 
                        href="tel:+13237841577" 
                        className="flex items-center gap-2 px-5 py-3 rounded-lg bg-cosmic-void/10 dark:bg-cosmic-nebula/10 text-cosmic-nebula font-medium transition-colors hover:bg-cosmic-void/15 dark:hover:bg-cosmic-nebula/15 w-full justify-center"
                      >
                        <Phone className="w-5 h-5" />
                        <span>(323) 784-1577</span>
                      </a>
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        Speak directly with an enterprise SEO strategist about your goals
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <CallToAction />
        </main>
      </PageTransition>
      
      <Footer />
    </div>
  );
};

export default EnterpriseSeo;
